import { useEffect, useRef } from 'react';

// =====================================================================
// Rascunho automático da tela de venda (Nova/Edição) em localStorage.
// Salva um snapshot do formulário com debounce a cada alteração para que
// o trabalho não-salvo sobreviva se a tela fechar (Cancelar, troca de
// menu, app encerrado) sem clicar em "Salvar". O snapshot NÃO inclui o
// status/andamento — esse já é gravado no servidor na hora.
//
// Chave por venda: `oa:rascunho-venda:novo` para criação e
// `oa:rascunho-venda:<id>` para edição.
// =====================================================================

const PREFIXO = 'oa:rascunho-venda:';

function chaveDe(idEdicao?: string): string {
  return `${PREFIXO}${idEdicao ?? 'novo'}`;
}

export function lerRascunhoVenda<T>(idEdicao?: string): T | null {
  try {
    const bruto = localStorage.getItem(chaveDe(idEdicao));
    return bruto ? (JSON.parse(bruto) as T) : null;
  } catch {
    // JSON inválido ou storage indisponível — trata como "sem rascunho".
    return null;
  }
}

export function limparRascunhoVenda(idEdicao?: string): void {
  try {
    localStorage.removeItem(chaveDe(idEdicao));
  } catch {
    // ignora — storage indisponível
  }
}

/**
 * Persiste `snapshot` no localStorage com debounce de 400 ms. `ativo`
 * mantém o autosave desligado até a carga inicial terminar e o usuário
 * decidir sobre um rascunho recuperável — assim o estado inicial não
 * sobrescreve o que estava guardado antes da decisão.
 *
 * A primeira passada (após `ativo`) fixa a "baseline" — o estado carregado
 * ou recuperado. Só grava quando o formulário DIVERGE da baseline (o
 * usuário mexeu em algo); se voltar ao original, remove o rascunho. Isso
 * evita gravar rascunho de um formulário só aberto (sobretudo em edição,
 * onde o estado inicial já vem preenchido).
 */
export function useSalvarRascunhoVenda<T>(
  idEdicao: string | undefined,
  snapshot: T,
  ativo: boolean,
): void {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const baseline = useRef<string | null>(null);
  const baselineDefinida = useRef(false);

  useEffect(() => {
    if (!ativo) return;
    const serial = JSON.stringify(snapshot);
    if (!baselineDefinida.current) {
      baseline.current = serial;
      baselineDefinida.current = true;
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try {
        if (serial === baseline.current) {
          localStorage.removeItem(chaveDe(idEdicao));
        } else {
          localStorage.setItem(chaveDe(idEdicao), serial);
        }
      } catch {
        // ignora — quota estourada ou valor não serializável
      }
    }, 400);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [idEdicao, snapshot, ativo]);
}
